import {
  addDoc,
  collection,
  doc,
  limit,
  onSnapshot,
  query,
  serverTimestamp,
  Timestamp,
  updateDoc,
  type Unsubscribe,
} from 'firebase/firestore';
import { db } from '../../../firebaseConfig.js';
import type { Notification } from './types';
import {
  NOTIFICATIONS_SUBCOLLECTION,
  USERS_COLLECTION,
} from './types';

function mapNotification(id: string, data: Record<string, unknown>): Notification {
  return {
    id,
    recipientUserId: String(data.recipientUserId ?? ''),
    actorUserId: String(data.actorUserId ?? ''),
    actorDisplayName: String(data.actorDisplayName ?? ''),
    actorUsername: String(data.actorUsername ?? ''),
    actorPhotoURL:
      typeof data.actorPhotoURL === 'string' && data.actorPhotoURL
        ? data.actorPhotoURL
        : null,
    type: (data.type as Notification['type']) || 'feedback_comment',
    postId: String(data.postId ?? ''),
    commentId: String(data.commentId ?? ''),
    postTitle: String(data.postTitle ?? ''),
    previewText: String(data.previewText ?? ''),
    read: Boolean(data.read),
    readAt: (data.readAt as Timestamp) ?? null,
    createdAt: (data.createdAt as Timestamp) ?? null,
    updatedAt: (data.updatedAt as Timestamp) ?? null,
    metadata:
      data.metadata && typeof data.metadata === 'object' && !Array.isArray(data.metadata)
        ? (data.metadata as Record<string, unknown>)
        : undefined,
  };
}

/**
 * Subscribe to the current user's notifications (recent first).
 * Use for the notification center dropdown.
 */
export function subscribeNotifications(
  recipientUserId: string,
  onData: (notifications: Notification[]) => void,
  onError: (e: Error) => void,
  options?: { limitCount?: number }
): Unsubscribe {
  const limitCount = options?.limitCount ?? 20;
  // Notifications are stored under `users/{userId}/notifications/*`
  // so rules can scope access to the current recipient.
  const baseCol = collection(
    db,
    USERS_COLLECTION,
    recipientUserId,
    NOTIFICATIONS_SUBCOLLECTION
  );
  const q = query(
    baseCol,
    limit(limitCount)
  );
  return onSnapshot(
    q,
    (snap) => {
      onData(
        snap.docs.map((d) => mapNotification(d.id, d.data() as Record<string, unknown>))
      );
    },
    (err) => onError(err instanceof Error ? err : new Error(String(err)))
  );
}

/**
 * Mark a single notification as read.
 */
export async function markNotificationRead(notificationId: string): Promise<void> {
  // We only support marking as read for the current recipient.
  // The notification document itself includes `recipientUserId`.
  // Callers should ensure they pass notifications that include this field.
  // (If missing, the update will fail fast.)
  throw new Error('markNotificationRead now requires recipientUserId; use the overload');
}

export async function markNotificationReadForRecipient(params: {
  recipientUserId: string;
  notificationId: string;
}): Promise<void> {
  const { recipientUserId, notificationId } = params;
  if (!recipientUserId?.trim()) {
    throw new Error('recipientUserId is required');
  }
  const ref = doc(
    db,
    USERS_COLLECTION,
    recipientUserId,
    NOTIFICATIONS_SUBCOLLECTION,
    notificationId
  );
  await updateDoc(ref, {
    read: true,
    readAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

/**
 * Mark all notifications for a user as read (V1: optional; batch by querying then updating).
 * Not implemented in V1 to keep scope lean; can add later.
 */
export async function markAllNotificationsRead(_recipientUserId: string): Promise<void> {
  // V1: skip; single-click mark-as-read only
}

export type CreateFeedbackCommentNotificationParams = {
  recipientUserId: string;
  actorUserId: string;
  actorDisplayName: string;
  actorUsername: string;
  actorPhotoURL: string | null;
  postId: string;
  commentId: string;
  postTitle: string;
  previewText: string;
};

/**
 * Create an in-app notification for a new comment on a feedback post.
 * Call only when the commenter is not the post author.
 */
export async function createFeedbackCommentNotification(
  params: CreateFeedbackCommentNotificationParams
): Promise<string> {
  const col = collection(
    db,
    USERS_COLLECTION,
    params.recipientUserId,
    NOTIFICATIONS_SUBCOLLECTION
  );
  const ref = await addDoc(col, {
    recipientUserId: params.recipientUserId,
    actorUserId: params.actorUserId,
    actorDisplayName: params.actorDisplayName,
    actorUsername: params.actorUsername,
    actorPhotoURL: params.actorPhotoURL ?? null,
    type: 'feedback_comment',
    postId: params.postId,
    commentId: params.commentId,
    postTitle: params.postTitle,
    previewText: params.previewText,
    read: false,
    readAt: null,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return ref.id;
}
